import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import Cookies from "js-cookie";
import { removeLicense } from "../../../../services/locationApp/licenseService";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import { CustomTheme } from "../../../../types/customTheme";

interface License {
  licenseCode: string;
  subscriptionId?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  license: License | null;
  locationId: string;
  onRemoved: () => void | Promise<void>;
}

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    dialogPaper: {
      borderRadius: 10,
    },
    reasonField: {
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: 10,
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: theme.palette.background.entity_background,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
    errorText: {
      color: theme.palette.error.main,
      fontSize: 12,
    },
  })
);

const getToken = () => Cookies.get("idToken") || null;

const LicenseRemoveModal: React.FC<Props> = ({
  open,
  onClose,
  license,
  locationId,
  onRemoved,
}) => {
  const classes = useStyles();
  const [removeReason, setRemoveReason] = useState("");
  const [confirmLicenseCode, setConfirmLicenseCode] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setRemoveReason("");
      setConfirmLicenseCode("");
      setError("");
      setIsLoading(false);
    }
  }, [open, license]);

  const hasLinkedSubscription = Boolean(license?.subscriptionId);
  const isLicenseCodeConfirmed =
    confirmLicenseCode.trim() === license?.licenseCode;
  const isRemoveDisabled =
    isLoading ||
    hasLinkedSubscription ||
    !removeReason.trim() ||
    !isLicenseCodeConfirmed;

  const handleRemove = async () => {
    if (!license || hasLinkedSubscription) {
      return;
    }

    const trimmedReason = removeReason.trim();

    if (!trimmedReason) {
      setError("Remove reason is required.");
      return;
    }

    if (!isLicenseCodeConfirmed) {
      setError("License code does not match.");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      const token = await getToken();

      if (!token) {
        throw new Error("Missing Firebase token");
      }

      await removeLicense(
        locationId,
        license.licenseCode,
        trimmedReason,
        token
      );

      await onRemoved();
      onClose();
    } catch (err) {
      const error = err as any;

      const responseBody = error?.response?.data || {};
      const message =
        responseBody?.message ||
        responseBody?.error ||
        error?.message ||
        "Failed to remove license. Please try again.";

      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>Remove License</DialogTitle>
      <DialogContent>
        {license ? (
          <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
            <Typography>
              your about to delete {license.licenseCode}?
            </Typography>

            {hasLinkedSubscription ? (
              <Typography
                className={classes.errorText}
                style={{ fontWeight: 600 }}
              >
                This license cannot be removed because it has a linked
                subscription.
              </Typography>
            ) : (
              <>
                <TextField
                  label="Remove Reason"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={removeReason}
                  className={classes.reasonField}
                  onChange={(e) => {
                    setRemoveReason(e.target.value);
                    setError("");
                  }}
                />
                <TextField
                  label="License Code"
                  variant="outlined"
                  fullWidth
                  value={confirmLicenseCode}
                  className={classes.reasonField}
                  onChange={(e) => {
                    setConfirmLicenseCode(e.target.value);
                    setError("");
                  }}
                />
              </>
            )}

            {error && (
              <Typography className={classes.errorText}>{error}</Typography>
            )}
          </Box>
        ) : (
          <Typography>No license selected</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <ButtonCommon
          variant="contained"
          color="orange"
          onClick={onClose}
          style={{ minWidth: 120, height: 36 }}
        >
          No
        </ButtonCommon>
        <ButtonCommon
          variant="contained"
          color="red"
          onClick={handleRemove}
          isLoadingPage={isLoading}
          isDisabled={isRemoveDisabled}
          style={{ minWidth: 140, height: 36 }}
        >
          Yes
        </ButtonCommon>
      </DialogActions>
    </Dialog>
  );
};

export default LicenseRemoveModal;
